import React, { useEffect, useState } from 'react';
import CustomSpinner from './CustomSpinner';

// Helper to format date consistently as YYYY-MM-DD in local time without UTC offset bugs
const formatDateKey = (date) => {
  if (!date) return '';
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const generateTimeSlots = (startTime, endTime, duration) => {
  if (!startTime || !endTime || !duration || duration <= 0) return [];
  const [startHours, startMinutes] = startTime.split(':').map(Number);
  const [endHours, endMinutes] = endTime.split(':').map(Number);

  const timeSlots = [];
  let currentMinutes = startHours * 60 + startMinutes;
  const endMinutesTotal = endHours * 60 + endMinutes;

  while (currentMinutes + duration <= endMinutesTotal) {
    const nextMinutes = currentMinutes + duration;
    timeSlots.push({
      startTime: formatTime(currentMinutes),
      endTime: formatTime(nextMinutes),
    });
    currentMinutes = nextMinutes;
  }

  return timeSlots;
};

const AvailabilityCalendarAndTimeSlots = ({ formData, setFormData }) => {
  const [activeDate, setActiveDate] = useState(new Date());
  const initialDateKey = formatDateKey(new Date());
  const [selectedDates, setSelectedDates] = useState([initialDateKey]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
      {/* Calendar View */}
      <div className="flex flex-col">
        <h2 className="text-xl font-bold text-gray-900 mb-2 border-l-4 border-blue-500 pl-3">
          1. Select Available Days
        </h2>
        <p className="text-xs text-gray-500 mb-3">
          Click a day to mark it available and select it. Click again to toggle selection. Double-click to remove availability.
        </p>
        <CalenderView
          selectedDate={activeDate}
          setSelectedDate={setActiveDate}
          availableDays={formData?.availability || {}}
          setAvailableDays={setFormData}
          selectedDates={selectedDates}
          setSelectedDates={setSelectedDates}
        />
      </div>

      {/* Time Slots Config */}
      <div className="flex flex-col">
        <h2 className="text-xl font-bold text-gray-900 mb-2 border-l-4 border-emerald-500 pl-3">
          2. Set Time Slots
        </h2>
        <p className="text-xs text-gray-500 mb-3">
          Configure and update time slots for your selected day(s).
        </p>
        <TimeSlotsConfig
          activeDate={activeDate}
          availableDays={formData?.availability || {}}
          setAvailableDays={setFormData}
          selectedDates={selectedDates}
          setSelectedDates={setSelectedDates}
        />
      </div>
    </div>
  );
};

const CalenderView = ({
  selectedDate,
  setSelectedDate,
  availableDays,
  setAvailableDays,
  selectedDates,
  setSelectedDates,
}) => {
  const daysInMonth = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      selectedDate.getFullYear() === today.getFullYear() &&
      selectedDate.getMonth() === today.getMonth() &&
      day === today.getDate()
    );
  };

  const changeMonth = (delta) => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + delta);
    setSelectedDate(newDate);
  };

  const handleDayClick = (day) => {
    const clickedDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
    const dateKey = formatDateKey(clickedDate);
    setSelectedDate(clickedDate);

    // If day is not in availableDays yet, add it as available and select it
    if (!availableDays?.[dateKey]) {
      const newDay = {
        a_status: 'Available',
        date: dateKey,
        timeSlots: [],
      };

      setAvailableDays((prev) => ({
        ...prev,
        availability: {
          ...(prev?.availability || {}),
          [dateKey]: newDay,
        },
      }));

      setSelectedDates((prev) => (prev.includes(dateKey) ? prev : [...prev, dateKey]));
    } else {
      // If already available, toggle selection in selectedDates
      setSelectedDates((prev) =>
        prev.includes(dateKey) ? prev.filter((d) => d !== dateKey) : [...prev, dateKey]
      );
    }
  };

  const handleDayDoubleClick = (day) => {
    const clickedDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
    const dateKey = formatDateKey(clickedDate);

    setAvailableDays((prev) => {
      const updatedAvailability = { ...(prev?.availability || {}) };
      delete updatedAvailability[dateKey];
      return { ...prev, availability: updatedAvailability };
    });
    setSelectedDates((prev) => prev.filter((d) => d !== dateKey));
  };

  // Quick weekday selection: selects all available days in the current month matching this weekday
  const handleSelectWeekday = (weekdayIndex) => {
    const totalDays = daysInMonth();
    const matchingDateKeys = [];

    for (let day = 1; day <= totalDays; day++) {
      const dateObj = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
      if (dateObj.getDay() === weekdayIndex) {
        const dateKey = formatDateKey(dateObj);
        matchingDateKeys.push(dateKey);
      }
    }

    // Auto-mark any un-marked matching days as available as well
    setAvailableDays((prev) => {
      const currentAvail = { ...(prev?.availability || {}) };
      matchingDateKeys.forEach((key) => {
        if (!currentAvail[key]) {
          currentAvail[key] = {
            a_status: 'Available',
            date: key,
            timeSlots: [],
          };
        }
      });
      return { ...prev, availability: currentAvail };
    });

    // Toggle: if all are already in selectedDates, deselect them; otherwise select all of them
    const allSelected = matchingDateKeys.every((key) => selectedDates.includes(key));
    if (allSelected) {
      setSelectedDates((prev) => prev.filter((d) => !matchingDateKeys.includes(d)));
    } else {
      setSelectedDates((prev) => Array.from(new Set([...prev, ...matchingDateKeys])));
    }
  };

  const handleSelectAllAvailable = () => {
    const allAvailableKeys = Object.keys(availableDays);
    setSelectedDates(allAvailableKeys);
  };

  const handleClearSelection = () => {
    setSelectedDates([]);
  };

  const weekdaysShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const renderDays = () => {
    const days = [];
    const totalDays = daysInMonth();

    for (let day = 1; day <= totalDays; day++) {
      const dateObj = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), day);
      const dateKey = formatDateKey(dateObj);

      const isAvailable = Boolean(availableDays?.[dateKey]);
      const isSelected = selectedDates.includes(dateKey);
      const today = isToday(day);
      const slotCount = availableDays?.[dateKey]?.timeSlots?.length || 0;

      let styleClasses = 'bg-white border-gray-200 text-gray-700 hover:border-blue-400';
      if (isSelected) {
        styleClasses = 'bg-emerald-600 text-white font-bold border-emerald-700 ring-2 ring-blue-500 shadow-sm';
      } else if (isAvailable) {
        styleClasses = 'bg-emerald-100 text-emerald-900 border-emerald-400 font-semibold hover:bg-emerald-200';
      }

      if (today && !isSelected) {
        styleClasses += ' ring-2 ring-blue-400';
      }

      days.push(
        <button
          key={day}
          type="button"
          onClick={() => handleDayClick(day)}
          onDoubleClick={() => handleDayDoubleClick(day)}
          title={`Date: ${dateKey} - Click to select/deselect, Double-click to remove`}
          className={`calendar-day relative flex flex-col items-center justify-center p-2 h-14 rounded-lg border text-sm transition-all duration-150 ${styleClasses}`}
        >
          <span className="text-sm">{day}</span>
          {isAvailable && (
            <span
              className={`text-[10px] px-1 rounded-full mt-0.5 ${
                isSelected ? 'bg-emerald-800 text-emerald-100' : 'bg-emerald-200 text-emerald-800'
              }`}
            >
              {slotCount > 0 ? `${slotCount} slots` : 'Available'}
            </span>
          )}
        </button>
      );
    }

    return days;
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Month Header */}
      <div className="flex items-center justify-between bg-blue-50 p-2.5 rounded-lg border border-blue-100">
        <button
          type="button"
          onClick={() => changeMonth(-1)}
          className="px-3 py-1 bg-white hover:bg-blue-100 text-blue-800 font-bold rounded-md shadow-xs transition"
        >
          &larr; Prev
        </button>
        <span className="font-bold text-gray-800 text-base">
          {selectedDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
        </span>
        <button
          type="button"
          onClick={() => changeMonth(1)}
          className="px-3 py-1 bg-white hover:bg-blue-100 text-blue-800 font-bold rounded-md shadow-xs transition"
        >
          Next &rarr;
        </button>
      </div>

      {/* Weekday Quick Selectors */}
      <div className="flex flex-wrap items-center gap-1.5 p-2 bg-gray-50 rounded-lg border border-gray-200 text-xs">
        <span className="font-semibold text-gray-600 mr-1">Quick Select:</span>
        {weekdaysShort.map((dayName, idx) => (
          <button
            key={dayName}
            type="button"
            onClick={() => handleSelectWeekday(idx)}
            className="px-2 py-1 bg-white hover:bg-blue-500 hover:text-white text-gray-700 font-medium rounded border border-gray-300 transition"
            title={`Select all ${dayName}s in this month`}
          >
            {dayName}
          </button>
        ))}
        <div className="ml-auto flex gap-1.5">
          <button
            type="button"
            onClick={handleSelectAllAvailable}
            className="px-2 py-1 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-semibold rounded border border-emerald-300 transition"
          >
            Select All
          </button>
          <button
            type="button"
            onClick={handleClearSelection}
            className="px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded transition"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="border border-gray-200 p-2.5 bg-gray-50 rounded-lg">
        <div className="grid grid-cols-7 gap-1.5 mb-1.5">
          {weekdaysShort.map((dayName) => (
            <div key={dayName} className="text-center font-bold text-xs text-gray-500 uppercase py-1">
              {dayName}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {Array.from(
            { length: new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1).getDay() },
            (_, i) => (
              <div key={`offset-${i}`} className="h-14"></div>
            )
          )}
          {renderDays()}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600 px-1">
        <div className="flex items-center gap-1.5">
          <span className="w-3.5 h-3.5 bg-emerald-600 rounded ring-1 ring-blue-500"></span>
          <span>Selected for Update</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3.5 h-3.5 bg-emerald-100 border border-emerald-400 rounded"></span>
          <span>Available Day</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3.5 h-3.5 bg-white border border-gray-300 rounded"></span>
          <span>Not Marked</span>
        </div>
      </div>
    </div>
  );
};

const TimeSlotsConfig = ({
  activeDate,
  availableDays,
  setAvailableDays,
  selectedDates,
  setSelectedDates,
}) => {
  const activeDateKey = formatDateKey(activeDate);
  const activeSlots = availableDays?.[activeDateKey]?.timeSlots || [];

  const handleRemoveSingleSlot = (timeSlotToRemove) => {
    setAvailableDays((prev) => {
      const updatedAvailability = { ...(prev?.availability || {}) };
      const currentDayData = updatedAvailability[activeDateKey];
      if (!currentDayData) return prev;

      updatedAvailability[activeDateKey] = {
        ...currentDayData,
        timeSlots: (currentDayData.timeSlots || []).filter(
          (slot) =>
            !(
              slot.startTime === timeSlotToRemove.startTime &&
              slot.endTime === timeSlotToRemove.endTime &&
              slot.isOnline === timeSlotToRemove.isOnline
            )
        ),
      };

      return { ...prev, availability: updatedAvailability };
    });
  };

  const handleRemoveDateFromSelection = (dateKey) => {
    setSelectedDates((prev) => prev.filter((d) => d !== dateKey));
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Selected Dates Summary Badge */}
      <div className="p-3 bg-blue-50/70 border border-blue-200 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="font-bold text-sm text-blue-950 flex items-center gap-2">
            <span>🎯 Target Days:</span>
            <span className="bg-blue-600 text-white text-xs px-2.5 py-0.5 rounded-full font-semibold">
              {selectedDates.length} Selected
            </span>
          </span>
          {selectedDates.length > 0 && (
            <button
              type="button"
              onClick={() => setSelectedDates([])}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium underline"
            >
              Deselect All
            </button>
          )}
        </div>

        {selectedDates.length === 0 ? (
          <p className="text-xs text-amber-700 italic">
            ⚠️ No days currently selected! Click dates on the calendar to select them for updates.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto p-1 bg-white rounded border border-blue-100">
            {selectedDates.map((dateKey) => (
              <span
                key={dateKey}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-900 text-xs rounded font-medium"
              >
                {dateKey}
                <button
                  type="button"
                  onClick={() => handleRemoveDateFromSelection(dateKey)}
                  className="hover:text-red-600 font-bold ml-0.5 text-xs"
                  title="Remove from selection"
                >
                  &times;
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Preview Slots for Currently Active Date */}
      <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-gray-700">
            Existing Slots for <span className="text-blue-700 font-bold">{activeDateKey}</span>:
          </span>
          <span className="text-xs text-gray-500 font-medium">
            {activeSlots.length} slot{activeSlots.length === 1 ? '' : 's'}
          </span>
        </div>

        <div className="max-h-28 overflow-y-auto p-2 bg-white rounded border border-gray-200 flex flex-wrap gap-2">
          {activeSlots.length === 0 ? (
            <span className="text-xs text-gray-400 italic py-1">No time slots configured for this date.</span>
          ) : (
            activeSlots.map((slot, idx) => (
              <div
                key={idx}
                className={`inline-flex items-center gap-2 px-2.5 py-1 rounded text-xs border ${
                  slot.isOnline
                    ? 'bg-blue-50 border-blue-300 text-blue-900'
                    : 'bg-purple-50 border-purple-300 text-purple-900'
                }`}
              >
                <span className="font-semibold">
                  {slot.startTime} - {slot.endTime}
                </span>
                <span className="text-[10px] font-medium">{slot.isOnline ? '🌐 Online' : '🏥 Offline'}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveSingleSlot(slot)}
                  className="text-gray-400 hover:text-red-600 font-bold ml-1"
                  title="Delete slot"
                >
                  &times;
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Slot Generation Form */}
      <AddTimeSlot
        activeDateKey={activeDateKey}
        availability={availableDays}
        setAvailability={setAvailableDays}
        selectedDates={selectedDates}
      />
    </div>
  );
};

const AddTimeSlot = ({ activeDateKey, availability, setAvailability, selectedDates }) => {
  const [startTime, setStartTime] = useState('09:00');
  const [selectedDuration, setSelectedDuration] = useState(15);
  const [endTime, setEndTime] = useState('13:00');
  const [slotType, setSlotType] = useState('online');

  // Add time slots to selected days (Append mode)
  const handleAddSlotsToSelected = () => {
    if (!selectedDates || selectedDates.length === 0) {
      alert('Please select at least one day from the calendar.');
      return;
    }

    const newSlots = generateTimeSlots(startTime, endTime, selectedDuration).map((slot) => ({
      ...slot,
      isOnline: slotType === 'online',
    }));

    if (newSlots.length === 0) {
      alert('No slots generated. Please check start time, end time, and duration.');
      return;
    }

    setAvailability((prev) => {
      const updatedAvailability = { ...(prev?.availability || {}) };

      selectedDates.forEach((dateKey) => {
        const existingDay = updatedAvailability[dateKey] || {
          a_status: 'Available',
          date: dateKey,
          timeSlots: [],
        };

        // Filter out duplicate slots by comparing startTime + isOnline
        const existingSlots = existingDay.timeSlots || [];
        const combinedSlots = [...existingSlots];

        newSlots.forEach((newSlot) => {
          const isDuplicate = combinedSlots.some(
            (s) => s.startTime === newSlot.startTime && s.isOnline === newSlot.isOnline
          );
          if (!isDuplicate) {
            combinedSlots.push(newSlot);
          }
        });

        // Sort chronologically
        combinedSlots.sort((a, b) => a.startTime.localeCompare(b.startTime));

        updatedAvailability[dateKey] = {
          ...existingDay,
          a_status: existingDay.a_status || 'Available',
          timeSlots: combinedSlots,
        };
      });

      return { ...prev, availability: updatedAvailability };
    });
  };

  // Replace time slots on selected days (Overwrite mode)
  const handleReplaceSlotsOnSelected = () => {
    if (!selectedDates || selectedDates.length === 0) {
      alert('Please select at least one day from the calendar.');
      return;
    }

    const confirmMsg = `Replace all time slots for ${selectedDates.length} selected day(s) with ${slotType.toUpperCase()} slots (${startTime} to ${endTime})?`;
    if (!window.confirm(confirmMsg)) return;

    const newSlots = generateTimeSlots(startTime, endTime, selectedDuration).map((slot) => ({
      ...slot,
      isOnline: slotType === 'online',
    }));

    setAvailability((prev) => {
      const updatedAvailability = { ...(prev?.availability || {}) };

      selectedDates.forEach((dateKey) => {
        const existingDay = updatedAvailability[dateKey];
        updatedAvailability[dateKey] = {
          ...(existingDay || {}),
          a_status: existingDay?.a_status || 'Available',
          date: dateKey,
          timeSlots: [...newSlots],
        };
      });

      return { ...prev, availability: updatedAvailability };
    });
  };

  // Clear time slots for selected days
  const handleClearSlotsOnSelected = () => {
    if (!selectedDates || selectedDates.length === 0) {
      alert('Please select at least one day from the calendar.');
      return;
    }

    if (!window.confirm(`Clear all time slots from ${selectedDates.length} selected day(s)?`)) return;

    setAvailability((prev) => {
      const updatedAvailability = { ...(prev?.availability || {}) };

      selectedDates.forEach((dateKey) => {
        if (updatedAvailability[dateKey]) {
          updatedAvailability[dateKey] = {
            ...updatedAvailability[dateKey],
            timeSlots: [],
          };
        }
      });

      return { ...prev, availability: updatedAvailability };
    });
  };

  return (
    <div className="flex flex-col gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
      {/* Slot Type Configuration */}
      <div className="flex flex-col gap-1">
        <label className="font-semibold text-xs text-gray-700">Slot Type:</label>
        <select
          value={slotType}
          onChange={(e) => setSlotType(e.target.value)}
          className="rounded border border-gray-300 p-1.5 text-xs bg-white focus:ring-1 focus:ring-blue-500"
        >
          <option value="online">🌐 Online Slot</option>
          <option value="offline">🏥 Offline (In-Clinic) Slot</option>
        </select>
      </div>

      {/* Time & Duration Configuration */}
      <div className="grid grid-cols-3 gap-2 items-end">
        <div className="flex flex-col gap-1">
          <label className="font-semibold text-xs text-gray-700">Start Time:</label>
          <input
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            className="rounded border border-gray-300 p-1.5 text-xs bg-white focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="font-semibold text-xs text-gray-700">End Time:</label>
          <input
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            className="rounded border border-gray-300 p-1.5 text-xs bg-white focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="font-semibold text-xs text-gray-700">Duration (min):</label>
          <input
            type="number"
            min="5"
            step="5"
            value={selectedDuration}
            onChange={(e) => setSelectedDuration(parseInt(e.target.value) || 15)}
            className="rounded border border-gray-300 p-1.5 text-xs bg-white text-center font-bold focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Action Buttons for Selected Days */}
      <div className="flex flex-col gap-2 mt-2 pt-2 border-t border-gray-200">
        <button
          type="button"
          onClick={handleAddSlotsToSelected}
          disabled={selectedDates.length === 0}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 px-3 text-sm font-bold shadow-xs transition disabled:opacity-50 flex items-center justify-center gap-1.5"
        >
          <span>➕</span>
          <span>
            {selectedDates.length <= 1
              ? `Add Slots to ${selectedDates[0] || 'Selected Day'}`
              : `Add Slots to ${selectedDates.length} Selected Days`}
          </span>
        </button>

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={handleReplaceSlotsOnSelected}
            disabled={selectedDates.length === 0}
            className="bg-amber-500 hover:bg-amber-600 text-white rounded-lg py-1.5 px-2 text-xs font-semibold shadow-xs transition disabled:opacity-50"
            title="Overwrites (replaces) any existing slots on the selected days with the new ones"
          >
            ↻ Overwrite on {selectedDates.length} Days
          </button>

          <button
            type="button"
            onClick={handleClearSlotsOnSelected}
            disabled={selectedDates.length === 0}
            className="bg-red-100 hover:bg-red-200 text-red-700 rounded-lg py-1.5 px-2 text-xs font-semibold transition disabled:opacity-50"
            title="Delete all slots on the selected days"
          >
            🗑️ Clear {selectedDates.length} Days
          </button>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityCalendarAndTimeSlots;
